/**
 * Convertidor de Editor.js JSON a HTML  
 * Convierte los bloques de Editor.js a HTML seguro para renderizar en el blog público
 */

export const editorJsToHtml = (editorData) => {
    if (!editorData) return '';
    
    let data;
    try {
        // Si es un string, intentar parsearlo
        if (typeof editorData === 'string') {
            // Si el string comienza con { y parece JSON, parsearlo
            if (editorData.trim().startsWith('{')) {
                data = JSON.parse(editorData);
            } else {
                // Si es texto plano corrupto, envolver en un párrafo
                console.warn('⚠️ Contenido no es JSON válido, mostrando como texto plano');
                return `<div class="content-error">
                    <p><strong>⚠️ El contenido de este artículo necesita ser editado nuevamente.</strong></p>
                    <p>Por favor, contacta al administrador.</p>
                </div>`;
            }
        } else if (typeof editorData === 'object') {
            data = editorData;
        } else {
            console.error('Tipo de dato inesperado:', typeof editorData);
            return '';
        }
    } catch (e) {
        console.error('❌ Error al parsear contenido de EditorJS:', e);
        console.error('Contenido recibido:', editorData?.substring?.(0, 200) || editorData);
        return `<div class="content-error">
            <p><strong>⚠️ Error al cargar el contenido de este artículo.</strong></p>
            <p>El contenido necesita ser editado nuevamente desde el CMS.</p>
        </div>`;
    }
    
    if (!data.blocks || !Array.isArray(data.blocks)) {
        console.error('El contenido no tiene estructura de EditorJS válida');
        return `<div class="content-error">
            <p><strong>⚠️ El contenido de este artículo tiene un formato incorrecto.</strong></p>
        </div>`;
    }

    return data.blocks.map(block => {
        switch (block.type) {
            case 'header':
                const level = block.data.level || 2;
                return `<h${level}>${block.data.text}</h${level}>`;
            
            case 'paragraph':
                return `<p>${block.data.text}</p>`;
            
            case 'list':
                const listType = block.data.style === 'ordered' ? 'ol' : 'ul';
                const items = block.data.items.map(item => `<li>${item}</li>`).join('');
                return `<${listType}>${items}</${listType}>`;
            
            case 'checklist':
                const checkItems = block.data.items.map(item => 
                    `<li class="checklist-item ${item.checked ? 'checked' : ''}">
                        <input type="checkbox" ${item.checked ? 'checked' : ''} disabled>
                        <span>${item.text}</span>
                    </li>`
                ).join('');
                return `<ul class="checklist">${checkItems}</ul>`;
            
            case 'image':
                const caption = block.data.caption ? `<figcaption>${block.data.caption}</figcaption>` : '';
                const stretched = block.data.stretched ? ' stretched' : '';
                const withBackground = block.data.withBackground ? ' with-background' : '';
                const withBorder = block.data.withBorder ? ' with-border' : '';
                const size = block.data.size ? ` image-size-${block.data.size}` : '';
                
                // Debug: Mostrar el tamaño de la imagen
                console.log('🖼️ Imagen:', {
                    url: block.data.file?.url,
                    size: block.data.size || 'sin tamaño',
                    claseGenerada: `image-block${size}`
                });
                
                return `<figure class="image-block${size}${stretched}${withBackground}${withBorder}">
                    <img src="${block.data.file.url}" alt="${block.data.caption || ''}">
                    ${caption}
                </figure>`;
            
            case 'quote':
                return `<blockquote>
                    <p>${block.data.text}</p>
                    ${block.data.caption ? `<cite>${block.data.caption}</cite>` : ''}
                </blockquote>`;
            
            case 'warning':
                return `<div class="warning-block">
                    <strong>${block.data.title || 'Advertencia'}</strong>
                    <p>${block.data.message}</p>
                </div>`;
            
            case 'button':
                const buttonLink = block.data.link || '#';
                const buttonText = block.data.text || 'Click aquí';
                return `<div class="button-block">
                    <a href="${buttonLink}" class="btn btn-primary" target="_blank" rel="noopener noreferrer">
                        ${buttonText}
                    </a>
                </div>`;
            
            case 'columns':
                const columnsCount = block.data.columns || 2;
                const columnsHtml = block.data.items.map((content, index) => 
                    `<div class="layout-column">${content}</div>`
                ).join('');
                return `<div class="layout-columns layout-cols-${columnsCount} style-${block.data.style || 'default'}">
                    ${columnsHtml}
                </div>`;
            
            case 'code':
                return `<pre><code>${escapeHtml(block.data.code)}</code></pre>`;
            
            case 'embed':
                return `<div class="embed-container">
                    <iframe src="${block.data.embed}" frameborder="0" allowfullscreen></iframe>
                    ${block.data.caption ? `<p class="embed-caption">${block.data.caption}</p>` : ''}
                </div>`;
            
            case 'table':
                const tableRows = block.data.content.map((row) => {
                    const cells = row.map(cell => `<td>${cell}</td>`).join('');
                    return `<tr>${cells}</tr>`;
                }).join('');
                return `<table class="editor-table">${tableRows}</table>`;
            
            case 'delimiter':
                return `<div class="delimiter">* * *</div>`;
            
            case 'attaches':
                const fileName = block.data.file?.name || 'Archivo adjunto';
                const fileSize = block.data.file?.size ? ` (${formatFileSize(block.data.file.size)})` : '';
                const fileUrl = block.data.file?.url || '#';
                return `<div class="attachment-block">
                    <a href="${fileUrl}" download class="attachment-link">
                        📎 ${fileName}${fileSize}
                    </a>
                </div>`;
            
            case 'raw':
                return block.data.html; // HTML crudo - cuidado con XSS
            
            default:
                console.warn(`Unknown block type: ${block.type}`);
                return '';
        }
    }).join('\n');
};

// Formatear tamaño de archivo
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Escape HTML para prevenir XSS
const escapeHtml = (text) => {
    const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
};

/**
 * Extrae texto plano del JSON de Editor.js (sin HTML)
 * Útil para generar excerpts o previsualizaciones
 */
export const editorJsToPlainText = (editorData, maxLength = 200) => {
    if (!editorData) return '';
    
    let data;
    try {
        data = typeof editorData === 'string' ? JSON.parse(editorData) : editorData;
    } catch (e) {
        console.error('Error parsing editor data:', e);
        return '';
    }
    
    if (!data.blocks || !Array.isArray(data.blocks)) {
        return '';
    }

    // Extraer texto de todos los bloques
    const textParts = data.blocks.map(block => {
        switch (block.type) {
            case 'header':
            case 'paragraph':
                return block.data.text || '';
            
            case 'list':
                return block.data.items ? block.data.items.join(' ') : '';
            
            case 'quote':
                return block.data.text || '';
            
            case 'code':
                return ''; // Ignorar código en el excerpt
            
            default:
                return '';
        }
    }).filter(text => text.length > 0);

    // Unir todo el texto y limitar longitud
    const fullText = textParts.join(' ').replace(/<[^>]*>/g, ''); // Remover cualquier HTML
    
    if (maxLength && fullText.length > maxLength) {
        return fullText.substring(0, maxLength).trim();
    }
    
    return fullText.trim();
};

export default editorJsToHtml;
